import React, { useState } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Download, FileText } from 'lucide-react';
import { message } from 'antd';
import { productsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

function BulkProductUploadModal({ isOpen, onClose, onUploadSuccess }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF';
  const themeColor = currentTheme?.color || '#0d9488';

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = async () => {
    try {
      message.loading({ content: 'Downloading template from API...', key: 'download' });
      await productsApi.downloadTemplate();
      message.success({ content: 'Template downloaded successfully!', key: 'download' });
    } catch (err) {
      console.error('Error downloading template from API:', err);
      message.error({ content: err.message || 'Failed to download template from API', key: 'download' });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const isValidType = validExtensions.some(ext => fileName.endsWith(ext));

      if (!isValidType) {
        message.warning('Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.warning('File size must be less than 10MB');
        return;
      }

      setUploadedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = handleFileChange;
    input.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadResult(null);
  };

  const handleSave = async () => {
    if (!uploadedFile) {
      message.warning('Please upload a file first');
      return;
    }

    setUploading(true);
    try {
      const response = await productsApi.bulkUploadExcel(uploadedFile);

      const result = {
        success: response.success ?? true,
        message: response.message || (response.successCount 
          ? `Successfully uploaded ${response.successCount} products`
          : 'Bulk upload completed'),
        totalProcessed: response.totalProcessed || (response.successCount || 0) + (response.failureCount || 0),
        successCount: response.successCount || 0,
        duplicateCount: response.duplicateCount || 0,
        failureCount: response.failureCount || (response.failedProducts ? response.failedProducts.length : 0),
        failedProducts: response.failedProducts || response.failedLeads || [],
        duplicateProducts: response.duplicateProducts || response.duplicateLeads || []
      };

      setUploadResult(result);

      if (onUploadSuccess) {
        onUploadSuccess(result);
      }

      if (result.success && result.failureCount === 0) {
        message.success('Upload completed successfully!');
        setTimeout(() => handleClose(), 2500);
      } else {
        message.warning('Upload completed with some warnings or errors');
      }
    } catch (err) {
      console.error('Error bulk uploading products:', err);
      setUploadResult({
        success: false,
        message: err.message || 'Failed to upload products',
        totalProcessed: 0,
        successCount: 0,
        duplicateCount: 0,
        failureCount: 1,
        failedProducts: [{ row: '1', product: uploadedFile.name, error: err.message || 'Server error occurred' }],
        duplicateProducts: []
      });
      message.error(err.message || 'Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setUploadedFile(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm transition-all duration-300">
      <div className={`rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border shadow-2xl transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-[#E8F5F0] border-teal-100'}`}>
        <div className={`sticky top-0 z-10 p-6 pb-4 border-b transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-[#E8F5F0] border-teal-200'}`}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>Add Bulk Products</h2>
            <button
              onClick={handleClose}
              className={`p-1 rounded-full transition-all ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
            >
              <X className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Upload Result Alert */}
          {uploadResult && (
            <div className={`mb-6 p-4 rounded-lg border-2 ${uploadResult.success
              ? 'bg-green-50 border-green-300'
              : 'bg-red-50 border-red-300'
              }`}>
              <div className="flex items-start space-x-3">
                {uploadResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className={`font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                    {uploadResult.message}
                  </h4>

                  {uploadResult.totalProcessed > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                        <p className={`text-lg font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{uploadResult.totalProcessed}</p>
                      </div>
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-green-500/5 border-green-500/10' : 'bg-white border-green-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-green-500/70' : 'text-green-600'}`}>Success</p>
                        <p className={`text-lg font-black ${isDark ? 'text-green-400' : 'text-green-600'}`}>{uploadResult.successCount}</p>
                      </div>
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-white border-red-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-red-500/70' : 'text-red-600'}`}>Failed</p>
                        <p className={`text-lg font-black ${isDark ? 'text-red-400' : 'text-red-600'}`}>{uploadResult.failureCount}</p>
                      </div>
                    </div>
                  )}

                  {uploadResult.failedProducts && uploadResult.failedProducts.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-red-800 mb-2">
                        Failed Products ({uploadResult.failedProducts.length}):
                      </p>
                      <div className="bg-white rounded-lg border border-red-200 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-red-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-semibold text-red-800">Row</th>
                              <th className="text-left p-2 font-semibold text-red-800">Product</th>
                              <th className="text-left p-2 font-semibold text-red-800">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.failedProducts.slice(0, 10).map((failed, index) => (
                              <tr key={index} className="border-t border-red-100">
                                <td className="p-2 text-gray-700 font-mono">{failed.row}</td>
                                <td className="p-2 text-gray-700">{failed.product || failed.productName || 'N/A'}</td>
                                <td className="p-2 text-red-700">{failed.error || failed.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {uploadResult.failedProducts.length > 10 && (
                          <div className="p-2 text-center text-xs text-red-600 bg-red-50 border-t border-red-100">
                            ...and {uploadResult.failedProducts.length - 10} more errors
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="space-y-6">
            {/* Step 1 - Download Template */}
            <div>
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm transition-all ${isDark ? 'bg-white/10 text-emerald-400' : 'bg-white text-teal-600'}`}>
                  <span className="font-black">1</span>
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Please download Excel file template.
                  </h3>
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Choose and download the template and fill in your products data.
                  </p>
                  <button
                    onClick={handleDownloadTemplate}
                    className={`px-6 py-2.5 border-2 rounded-lg transition-all flex items-center space-x-2 font-bold shadow-sm active:scale-95 ${isDark ? 'bg-white/5 border-emerald-500/50 text-emerald-400 hover:bg-white/10' : 'bg-white border-teal-500 text-teal-700 hover:bg-teal-50'}`}
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Template</span>
                  </button>

                  {/* Template Info */}
                  <div className={`mt-3 p-3.5 rounded-xl border ${isDark ? 'bg-blue-500/10 border-blue-500/20 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                    <div className="flex items-start space-x-2">
                      <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-bold mb-1">Template includes:</p>
                        <ul className="list-disc list-inside space-y-0.5 opacity-90">
                          <li>Required fields: SKU, Product Name, Category, Price</li>
                          <li>Optional fields: Description, MRP, Stock Quantity, HSN Code</li>
                          <li>Pre-filled sample product rows</li>
                          <li>Format: Excel (.xlsx)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 - Upload File */}
            <div>
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm transition-all ${isDark ? 'bg-white/10 text-emerald-400' : 'bg-white text-teal-600'}`}>
                  <span className="font-black">2</span>
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Please upload updated Excel file template.
                  </h3>
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Please upload filled data here.
                  </p>

                  {/* Upload Area */}
                  {!uploadedFile ? (
                    <div
                      onClick={handleUpload}
                      className={`border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all flex flex-col items-center justify-center space-y-3 ${isDark ? 'bg-white/5 border-white/10 hover:border-emerald-500 hover:bg-white/10' : 'bg-white border-teal-300 hover:border-teal-500 hover:bg-teal-50'}`}
                    >
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-teal-50'}`}>
                        <Upload className="w-8 h-8 text-teal-500" />
                      </div>
                      <div className="text-center">
                        <button className="text-teal-600 font-bold hover:text-teal-700">
                          Click to Upload
                        </button>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          CSV, XLSX or XLS (Max 10MB)
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={`border-2 rounded-xl p-4 shadow-sm ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-teal-300'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10' : 'bg-teal-50'}`}>
                            <FileText className="w-6 h-6 text-teal-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                            {uploading && (
                              <div className="w-full h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                                <div
                                  className="h-full bg-teal-500 rounded-full transition-all duration-300"
                                  style={{ width: '75%' }}
                                >
                                  <div className="w-full h-full bg-teal-400 rounded-full animate-pulse"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          disabled={uploading}
                          className={`p-2 rounded-full transition-colors ml-2 disabled:opacity-50 ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                        >
                          <X className="w-5 h-5 text-gray-400" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 p-6 pt-4 border-t flex justify-end transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-[#E8F5F0] border-teal-200'}`}>
          <button
            onClick={handleSave}
            disabled={!uploadedFile || uploading}
            className="px-8 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all font-black text-xs uppercase tracking-widest disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg active:scale-95"
            style={uploadedFile && !uploading ? { backgroundColor: themeColor } : {}}
          >
            {uploading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span>{uploading ? 'Uploading...' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkProductUploadModal;